import { AGFCreditAnalystAssignedFilter, AGFCustomFilters } from '@indigo-ag/schema';
import { PureQueryOptions } from 'apollo-client';
import gql from 'graphql-tag';
import { TableSort, calculateAfterPagination } from '../../../shared';
import { buildPaginationQueryParams } from './utils';

const financingInfo = `
  id
  date
  updatedAt
  prioritySince
  hasActiveConversations
  completedAt
  collateralType
  currentStates
  creditAnalystAssigned {
    id
    firstName
    lastName  
  }  
  campaign {
    id
    name
    isExternal
  }
  phase1 {
    status
    updatedAt
  }
  phase2 {
    status
    collateral {
      status
    }
    fieldMonitoring {
      startedAt
      completedAt
    }
    credit {
      status
    }
    matriculas {
      id
      kml {
        id
      }
    }
  }
  grower {
    id
    name
    cpfOrCnpj
    leadType
    state {
      id
      name
    }
  }
  orderInfo {
    opportunities {
      name
      collateralGroup
      contractValue
      backgroundCheckType
      type
    }
  }
`;

export const financingAgFinancingQueue = gql`
  query financingAgFinancingQueue(
    $pagination: AGFPagination!
    $filters: AGFilters
    $customFilters: AGFCustomFilters
    $sort: AGFSort
  ) {
    financingAgFinancingQueue(pagination: $pagination, filters: $filters, customFilters: $customFilters, sort: $sort) {
      ${buildPaginationQueryParams(financingInfo)}
    }
  }
`;

export const getAgFinancingQueueQuery = (
  rowsPerPage: number,
  currentPage: number,
  searchTextQuery: string,
  customFilters: AGFCustomFilters,
  sort: TableSort | null,
  analystSearchValue?: AGFCreditAnalystAssignedFilter
): PureQueryOptions => {
  return {
    query: financingAgFinancingQueue,
    variables: {
      pagination: { first: rowsPerPage, after: calculateAfterPagination(currentPage, rowsPerPage) },
      filters: { query: searchTextQuery },
      customFilters: !searchTextQuery || !!analystSearchValue ? customFilters : undefined,
      sort: sort ? { id: sort.id, direction: sort.direction?.toUpperCase() } : null
    }
  };
};

export const AgFinancingTotals = gql`
  query AgFinancingTotals($filters: AGFCustomFilters) {
    AgFinancingTotals(filters: $filters) {
      backgroundCheck {
        sent
        pending
        waitingCreditManagerApproval
      }
      creditAnalysis {
        pending
        inProgress
        awaitingApproval
        withCommercial
        phaseApproval
        rejected
        awaitingCommercialApproval
        rejectedCredits
        approvedCredits
      }
      collateralAnalysis {
        awaitingApproval
        inProgress
        withCommercial
        approvedCollaterals
        rejectedCollaterals
      }
      documentRegistration {
        activationPending
        waitingDraft
        draftEmitted
        reviewingDocuments
        creditAnalystApproval
        waitingCreditManagerApproval
        withCommercial
        waitingAmendment
        pendingContractValidation
      }
      externalCredit {
        newNegotiation
        inAnalysis
        onHold
        analysisSent
      }
      creditLead {
        pendingBackgroundCheck
        sent
        pendingApproval
        pendingDocumentsApproval
        onHold
        generateDraft
        waitingDocuments
        reviewingDocuments
        requiresLeadApproval
        requiresLeadApprovalBackgroundCheck
      }
      fieldMonitor {
        newNegotiation
        inProgress
        withCommercial
        completed
      }
      externalLegal {
        documentsValidated
        generateDraft
        inAnalysis
        newNegotiation
        reviewingDocuments
        waitingDocuments
        waitingAmendment
        waitingDigitalSignatures
        onHold
      }
    }
  }
`;

export const getAgFinancingTotalsQuery = (customFilters: AGFCustomFilters): PureQueryOptions => {
  return {
    query: AgFinancingTotals,
    variables: {
      filters: customFilters
    }
  };
};
